import { useMemo } from "react";
import useRouteName from "../../../hooks/useRouteName.js";
import { useSelector } from "react-redux";
import { getAncillaries, getFlightDetailsFetchStatus } from "../../../store/flightDetails/selectors.js";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { priceBreakdownUtils, localizationUtils, isFeatureRunningClientSide, ryanairUtils } from "@flights/web-api-utils-universal";
import usePointOfSale from "../../../hooks/usePointOfSale.js";
import { getExtraProducts, getPreOptedAncillaries } from "../../../ancillaries/store/extraProducts/selectors.js";
import { transformPreOptedExtras } from "./transforms/preOptedExtras.js";
import uniq from "lodash/uniq";
import { transformDisclaimersForExtras, transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers.js";
import useEvents from "../../../hooks/useEvents.js";
import isEmpty from "lodash/isEmpty";
import { getAttachedAncillaries } from "./helpers/index.js";
export function useUnifiedPriceBreakdownFlightCardPriceController(props) {
    const { flight } = props;
    const i18n = useI18n();
    const ipCountry = usePointOfSale();
    const routeName = useRouteName();
    const { squeak } = useEvents();
    const ancillaries = useSelector(getAncillaries);
    const extraProducts = useSelector(getExtraProducts);
    const preOptedAncillaries = useSelector(getPreOptedAncillaries);
    const fetchStatus = useSelector(getFlightDetailsFetchStatus);
    const segments = useMemo(() => flight?.segments, [flight]);
    const isHungaryDepartingRyanAir = useMemo(() => localizationUtils.isHungaryDepartingRyanAirFlight(segments), [segments]);
    const baseOptions = useMemo(() => ({
        pointOfSale: (ipCountry || ""),
        isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
        segments: segments || [],
        shouldUseNewLogicForTravelInsuranceTaxTitle: isFeatureRunningClientSide("FLIGHTS_FUNNEL_EXTRAS_TAX_TITLE_NEW_LOGIC"),
        shouldShowSubtitleForTravelInsurance: isFeatureRunningClientSide("FLIGHTS_STRATEGY_MANAGER_ENABLE_INSURANCE_DISCLAIMER_IN_UPB")
    }), [ipCountry, isHungaryDepartingRyanAir, segments]);
    const unifiedPriceBreakdown = useMemo(() => {
        const upb = flight?.unifiedPriceBreakdown;
        if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
            squeak(`UnifiedPriceTag::useUnifiedPriceBreakdownFlightCardPriceController - product id is missing - route "${routeName}"`);
        }
        return upb;
    }, [flight, routeName, squeak]);
    const allAncillaries = useMemo(() => {
        return !isEmpty(extraProducts) ? extraProducts : !isEmpty(ancillaries) ? ancillaries : flight?.ancillaries || {};
    }, [flight, ancillaries, extraProducts]);
    const preOptedExtras = useMemo(() => transformPreOptedExtras(preOptedAncillaries, allAncillaries), [preOptedAncillaries, allAncillaries]);
    const addedItems = useMemo(() => {
        if (!routeName.includes("flightdetails"))
            return [];
        return priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(i18n, preOptedExtras, baseOptions);
    }, [routeName, i18n, baseOptions, preOptedExtras]);
    const isFlightDetailsLoading = useMemo(() => {
        if (!routeName.includes("flightdetails"))
            return false;
        return fetchStatus === "loading";
    }, [fetchStatus, routeName]);
    const flightDetailsLoadingText = useMemo(() => {
        return i18n.trans(t("flights_price_breakdown_loading_details"));
    }, [i18n]);
    const disclaimers = useMemo(() => {
        return uniq([
            ...transformDisclaimersForPriceBreakdown(flight?.priceBreakdown),
            ...transformDisclaimersForExtras(preOptedExtras)
        ]);
    }, [flight, preOptedExtras]);
    const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);
    const expandedByDefault = useMemo(() => {
        return isRyanair
            ? {
                FLIGHT_ADULT: true,
                FLIGHT_CHILD: true,
                FLIGHT_INFANT: true,
                EXTRA: true
            }
            : undefined;
    }, [isRyanair]);
    const attachedAncillaries = useMemo(() => getAttachedAncillaries(unifiedPriceBreakdown, addedItems), [unifiedPriceBreakdown, addedItems]);
    return {
        unifiedPriceBreakdown,
        isHungaryDepartingRyanAir,
        isFlightDetailsLoading,
        expandedByDefault,
        disclaimers,
        flightDetailsLoadingText,
        addedItems,
        attachedAncillaries
    };
}
